import {QuoteDataSummary} from "../../../domain/out/quote-data-summary";
import {QuoteData} from "../../../domain/in/quote-data";
import {QuoteSummary} from "../../../domain/out/quote-summary";
import {OpportunitySummary} from "../../../domain/out/opportunity-summary";
import {Quote} from "../../../domain/in/quote/quote";
import {QuoteServiceSummary} from "../../../domain/out/quote-service-summary";
import {QuoteService} from "../../../domain/in/quote/quote-service";
import {ClientInfos} from "../../../domain/in/quote/client-informations";
import {ClientInfosSummary} from "../../../domain/out/client-informations-summary";
import {ExtraFeeSummary} from "../../../domain/out/extra-fee-summary";
import {ExtraFee} from "../../../domain/in/quote/extra-fee";
import {EmployeeRoleEnum} from "../../../domain/enum/employee-role.enum";
import {TranslationServiceInterface} from "../../translation-service.interface";
import {bigToString, parseBig} from "../../../utils/math-utils";
import {Big} from "big.js";
import {getAmountWithCurrency, getFormattedDate, getTotalAmountWithCurrency} from "../../../utils/string-utils";
import {CustomGroupSummaryMapper} from "./custom-group-summary.mapper";
import {TotalBillingStockMapper} from "./total-billing-stock.mapper";
import {Opportunity} from "../../../domain/in/opportunity";
import {EmployeeSummary} from "../../../domain/out/employee-summary";
import {Employee} from "../../../domain/in/employee";
import {ServiceLine} from "../../../domain/in/quote/service-line";
import {ServiceLineSummary} from "../../../domain/out/service-line-summary";

export class SynthesisService {
    private totalBillingStockMapper: TotalBillingStockMapper;

    constructor(protected customGroupSummaryMapper: CustomGroupSummaryMapper,
                protected translationService: TranslationServiceInterface) {
        this.totalBillingStockMapper = new TotalBillingStockMapper();
    }

    public async getSynthesisFromQuoteData(quoteData: QuoteData): Promise<QuoteDataSummary> {
        return new QuoteDataSummary(
            await this.getSynthesisFromQuote(quoteData.quote, quoteData.opportunity, quoteData.personalization),
            this.getOpportunitySummary(quoteData.opportunity));
    }

    public async getSynthesisFromQuote(quote: Quote, opportunity: Opportunity, personalization: Map<string, any>): Promise<QuoteSummary> {
        const {totalWeeklyAmount, totalMonthlyAmount} = this.getTotalAmounts(quote, personalization);
        return new QuoteSummary(
            quote.reference,
            totalWeeklyAmount,
            totalMonthlyAmount,
            await this.getServiceLinesSummary(opportunity.employee.roleCode.eForceRole, quote, personalization),
            this.getClientInfosSummary(opportunity.client),
            getFormattedDate(quote.creationDate, quote.locale),
            quote.countryIsoCode,
            opportunity.employee.roleCode,
            await this.getTranslation(personalization.get("securityDeposit"), quote.locale),
            await this.getTranslation(personalization.get("paymentMethod"), quote.locale),
            await this.getTranslation(personalization.get("paymentCondition"), quote.locale),
            personalization.get("withPresentationPages") === "true",
            this.getBevAmount(quote, quote.locale),
            personalization.get("showRenewalContent") === "true",
            this.getBevNewQuoteServiceAmount(quote, quote.locale)
        );
    }

    private getTotalAmounts(quote: Quote, personalization: Map<string, any>) {
        let totalWeeklyAmount = getTotalAmountWithCurrency(quote.totalWeeklyAmount, quote.currency, quote.locale);
        let totalMonthlyAmount = getTotalAmountWithCurrency(quote.totalMonthlyAmount, quote.currency, quote.locale);
        if (personalization.get("showRenewalContent") === "false") {
            let monthlyAmount = new Big(0);
            let weeklyAmount = new Big(0);
            quote.serviceLines.forEach(serviceLine => {
                monthlyAmount = monthlyAmount.add(parseBig(serviceLine.newQuoteService?.monthlyAmount));
                weeklyAmount = weeklyAmount.add(parseBig(serviceLine.newQuoteService?.weeklyAmount));
            });

            totalWeeklyAmount = getTotalAmountWithCurrency(bigToString(weeklyAmount), quote.currency, quote.locale);
            totalMonthlyAmount = getTotalAmountWithCurrency(bigToString(monthlyAmount), quote.currency, quote.locale);
        }

        return {totalWeeklyAmount, totalMonthlyAmount};
    }

    private getBevAmount(quote: Quote, locale: string) {
        const monthlyAmount = quote.serviceLines
            .filter(sl => sl.code === 'CAF' || sl.code === 'FON')
            .filter(sl => sl.isServicePriced())
            .map(sl => {
                return sl.monthlyAmount;
            });
        const sumMonthlyAmount = bigToString(monthlyAmount.map(amount =>
            parseBig(amount)).reduce((acc, val) => acc.add(val), new Big(0)));
        return getTotalAmountWithCurrency(sumMonthlyAmount, quote.currency, locale);
    }

    private getBevNewQuoteServiceAmount(quote: Quote, locale: string) {
        const monthlyAmount = quote.serviceLines?.filter(sl => sl.code === 'CAF' || sl.code === 'FON').map(sl => {
            return sl?.newQuoteService?.monthlyAmount;
        });
        const sumMonthlyAmount = bigToString(monthlyAmount.map(amount =>
            parseBig(amount)).reduce((acc, val) => acc.add(val), new Big(0)));
        return getTotalAmountWithCurrency(sumMonthlyAmount, quote.currency, locale);
    }

    private async getServiceLinesSummary(eForceRoleCode: string, quote: Quote, personalization: Map<string, any>): Promise<ServiceLineSummary[]> {
        const serviceLines = quote.serviceLines;
        if (!serviceLines) {
            return [];
        }

        return await Promise.all(
            serviceLines
                .filter(sl => sl.isServicePriced())
                .map(serviceLine => {
                        const view = this.getServiceLineView(serviceLine.code, personalization);
                        return this.getServiceLineSummary(serviceLine, eForceRoleCode, quote.currency, view, quote.locale);
                    }
                )
        );
    }

    public async getServiceLineSummary(serviceLine: ServiceLine, eForceRole: string, currency: string, view: string, locale: string): Promise<ServiceLineSummary> {
        return new ServiceLineSummary(
            serviceLine.code,
            await this.getServiceLabel(serviceLine.code, eForceRole, locale),
            getTotalAmountWithCurrency(serviceLine.appliedPrice, currency, locale),
            getTotalAmountWithCurrency(serviceLine.monthlyAmount, currency, locale),
            getTotalAmountWithCurrency(serviceLine.weeklyAmount, currency, locale),
            view,
            await this.getQuoteServiceSummary(currency, eForceRole, view, locale, serviceLine.newQuoteService),
            undefined
        );
    }

    public async getQuoteServiceSummary(currency: string, eForceRoleCode: string, view: string, locale: string, service?: QuoteService) {
        const role = EmployeeRoleEnum.getEmployeeRoleEnumFromEforceRole(eForceRoleCode);
        if (!service) {
            return undefined;
        }

        const customGroupsSummary = (await this.customGroupSummaryMapper.getCustomGroupSummary(service.customGroups, role, service.code, currency, view, locale, service.displayMode))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const quoteServiceSummary = new QuoteServiceSummary(
            service.code,
            service.contractInfoInputs,
            getAmountWithCurrency(service.appliedPrice, currency, locale),
            this.getExtraFeesSummary(service.extraFees, currency, locale),
            service.isCustomGroups,
            customGroupsSummary,
            this.getAdditionalStockAmount(service.additionalStockAmount, currency, locale),
            !!service.contractInfoInputs?.isStockSuppOffered,
            getTotalAmountWithCurrency(service.monthlyAmount, currency, locale),
            getTotalAmountWithCurrency(service.weeklyAmount, currency, locale),
            this.getTotalBillingStock(service, role, currency, locale),
            service.salesType,
            service.deviceConsumableLinks
        );

        return quoteServiceSummary;
    }

    private getTranslation(key: string, locale: string) {
        if (!key) {
            return null;
        }
        return this.translationService.translate('LABEL_' + key?.toUpperCase(), locale);
    }

    private getAdditionalStockAmount(additionalStockAmount: string, currency: string, locale: string) {
        if (parseBig(additionalStockAmount).gt(new Big(0))) {
            return getTotalAmountWithCurrency(additionalStockAmount, currency, locale);
        } else {
            return '';
        }
    }


    private getTotalBillingStock(service: QuoteService, role: EmployeeRoleEnum, currency: string, locale: string): string {
        return this.totalBillingStockMapper.getTotalBillingStock(service, role, currency, locale);

    }

    private getClientInfosSummary(clientInfos: ClientInfos): ClientInfosSummary {
        return new ClientInfosSummary(
            clientInfos.name,
            clientInfos.contact,
            clientInfos.city,
            clientInfos.phone,
            clientInfos.email,
            clientInfos.accountElisId
        );
    }


    private getEmployeeSummary(employee: Employee): EmployeeSummary {
        return new EmployeeSummary(
            employee.roleCode.pricingRole,
            employee.email,
            employee.firstname,
            employee.lastname,
            employee.phone);
    }

    private getExtraFeesSummary(extraFees: ExtraFee[], currency: string, locale: string): ExtraFeeSummary[] {
        return extraFees
            .map(fee => {
                const isPercentage = fee.type === 'PERCENTAGE_EXTRA_FEE';
                const calculatedAmount = isPercentage ? fee.totalAmount : fee.uniteAmount;
                const quantity = isPercentage ? `${fee.quantity} %` : fee.quantity;
                return new ExtraFeeSummary(
                    getAmountWithCurrency(fee.uniteAmount, currency, locale),
                    getAmountWithCurrency(calculatedAmount, currency, locale),
                    fee.label,
                    quantity,
                    getTotalAmountWithCurrency(fee.totalAmount, currency, locale)
                );
            });
    }


    public getServiceLineView(code: string, personalization: Map<string, any>): string {
        const synthesisViews = Array.from(personalization.keys()).filter(key => key.includes("synthesisView"));

        let synthesisViewCode = "";

        for (const viewKey of synthesisViews) {
            if (viewKey.toUpperCase().endsWith(code)) {
                synthesisViewCode = personalization.get(viewKey);
            }
        }

        return synthesisViewCode;
    }

    private getOpportunitySummary(opportunity: Opportunity): OpportunitySummary {
        return new OpportunitySummary(
            this.getClientInfosSummary(opportunity.client),
            this.getEmployeeSummary(opportunity.employee)
        );

    }

    protected async getServiceLabel(codeService: string, eForceRole: string, locale: string) {
        return this.translationService.translate('LABEL_' + codeService + '_SERVICE', locale);
    }
}
